import React, { useEffect, useState } from "react";
import { Table, Card, Button, Input, Space, Row, Col, Typography, message, Modal, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../../configs";
import { canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;
const { Search } = Input;

export default function UserList() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [userLevels, setUserLevels] = useState({});

    useEffect(() => {
        const fetchUserLevels = async () => {
            try {
                const res = await axiosInstance.get("/roles");
                const levelsMap = {};
                res.data.forEach(role => {
                    levelsMap[role.id_user_level] = {
                        label: role.user_level,
                        color: getColorByLevel(role.id_user_level)
                    };
                });
                setUserLevels(levelsMap);
            } catch (err) {
                console.error("Failed to fetch user levels:", err);
            }
        };
        fetchUserLevels();
    }, []);

    const getColorByLevel = (level) => {
        const colors = ["red", "blue", "green", "purple", "orange", "cyan", "gold", "magenta"];
        return colors[(level - 1) % colors.length] || "default";
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/user", {
                params: { search }
            });
            setData(res.data || []);
        } catch (err) {
            console.error(err);
            message.error("Gagal mengambil data user");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [search]);

    const handleDelete = (id, username) => {
        Modal.confirm({
            title: "Hapus User?",
            content: `User "${username}" akan dihapus.`,
            okText: "Ya, Hapus",
            okType: "danger",
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/user/${id}`);
                    message.success("User berhasil dihapus");
                    fetchData();
                } catch (e) {
                    message.error(e.response?.data?.error || "Gagal menghapus user");
                }
            }
        });
    };

    const columns = [
        {
            title: "Username",
            dataIndex: "username",
            width: 120,
            ellipsis: true,
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: "Nama",
            dataIndex: "name",
            width: 150,
            ellipsis: true,
        },
        {
            title: "Level",
            dataIndex: "id_user_level",
            width: 100,
            responsive: ["sm"],
            render: (val) => {
                const level = userLevels[val] || { label: `Lvl ${val}`, color: "default" };
                return <Tag color={level.color}>{level.label}</Tag>;
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 80,
            responsive: ["md"],
            render: (val) => (
                val === 1
                    ? <Tag color="green">Aktif</Tag>
                    : <Tag>Non</Tag>
            )
        },
        {
            title: "Aksi",
            key: "action",
            width: 90,
            align: "center",
            fixed: "right",
            render: (_, r) => (
                <Space size="small">
                    <Link to={`/referensi/users/edit/${r.id_user}`}>
                        <Button icon={<EditOutlined />} size="small" type="primary" />
                    </Link>
                    {canDelete(MODULES.USER) && (
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => handleDelete(r.id_user, r.username)}
                        />
                    )}
                </Space>
            )
        }
    ];

    return (
        <Card bodyStyle={{ padding: "16px" }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={12}>
                    <Title level={4} style={{ margin: 0 }}>Daftar User</Title>
                </Col>
                <Col xs={24} md={12}>
                    <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
                        <Link to="/referensi/users/create">
                            <Button type="primary" icon={<PlusOutlined />}>Tambah</Button>
                        </Link>
                    </Space>
                </Col>
            </Row>

            <Row style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Search
                        placeholder="Cari username atau nama..."
                        onSearch={val => setSearch(val)}
                        allowClear
                        enterButton
                    />
                </Col>
            </Row>

            <Table
                dataSource={data}
                columns={columns}
                rowKey="id_user"
                loading={loading}
                size="small"
                scroll={{ x: 500 }}
                pagination={{ pageSize: 20, size: "small", showTotal: (t) => `${t} data` }}
            />
        </Card>
    );
}
